// src/pages/categoryItem/CategoryItemScreen.tsx
import React from "react";
import { MdCategory } from "react-icons/md";
import { useTheme } from "../../common/hooks/useTheme";
import { ProductHeader } from "../../common/components/productHeader/ProductHeader";
import { PageMeta } from "../../common/components/pageMeta/PageMeta";
import { EmptyState } from "../../common/components/emptyState/EmptyState";
import Search from "../../common/components/search/Search";
import { useCategoryItemScreen } from "./useCategoryItemScreen";
import { getImageUri } from "../../common/utils/functions/getImageUri";
import styles from "./CategoryItemScreen.module.scss";
import ItemList from "../main/components/itemList/ItemList";
import { Share } from "../../common/components/share/Share";
import ChildrenList from "./components/childrenList/ChildrenList";
import { EntityType } from "../../common/types/enums";
import { Documents } from "../../common/components/documents/Documents";

export default function CategoryItemScreen() {
  const { theme } = useTheme();
  const {
    category,
    items,
    getCategoryName,
    isAuth,
    usingStock,
    handleGroupPress,
    searchQuery,
    handleSearchChange,
    handleSearchClear,
    loadingMore,
    hasMore,
    loadMore,
    isFirstLoading,
    totalItems,
  } = useCategoryItemScreen();

  if (isFirstLoading) {
    return (
      <div
        className={styles.container}
        style={{ backgroundColor: theme.background }}
      />
    );
  }

  if (!category) {
    return (
      <div
        className={styles.container}
        style={{ backgroundColor: theme.background }}
      >
        <ProductHeader
          isFavorite={false}
          title="Категория не найдена"
          showFavorite={false}
          textColor={theme.text}
          borderColor={theme.border}
        />
        <div className={styles.emptyWrapper}>
          <EmptyState
            icon={MdCategory}
            title="Категория не найдена"
            description="Запрашиваемая категория не существует или была удалена"
          />
        </div>
      </div>
    );
  }

  const children = category.children || [];
  const hasChildren = children.length > 0;
  const hasCategoryInfo =
    category.files?.[0] || category.description || hasChildren;

  return (
    <div
      className={styles.container}
      style={{ backgroundColor: theme.background }}
    >
      <PageMeta
        itemName={category.name}
        itemDescription={category.description}
        itemImageFile={category.files?.[0]}
        type="website"
      />

      <ProductHeader
        isFavorite={false}
        title={category.name}
        showFavorite={false}
        textColor={theme.text}
        borderColor={theme.border}
      />

      <div className={styles.searchWrapper}>
        <div className={styles.searchRow}>
          <Search
            value={searchQuery}
            onChange={handleSearchChange}
            clear={handleSearchClear}
            placeHolder="Поиск по товарам категории"
          />
          <Share
            customUrl={window.location.href}
            customText={
              searchQuery
                ? `Поиск: ${searchQuery}`
                : `Категория: ${category.name}`
            }
          />
        </div>
      </div>

      <div className={styles.content}>
        {!searchQuery && hasCategoryInfo && (
          <div className={styles.categoryInfo}>
            {category.files?.[0] && (
              <div className={styles.imageWrapper}>
                <img
                  src={`${getImageUri()}${category.files[0]}`}
                  alt={category.name}
                  className={styles.image}
                />
              </div>
            )}
            {category.description && (
              <p className={styles.description}>{category.description}</p>
            )}
            {hasChildren && <ChildrenList children={children} />}
            <Documents
              entityType={EntityType.CATEGORY}
              entityId={Number(category.id)}
            />
          </div>
        )}

        <div className={styles.itemsSection}>
          <h2 className={styles.itemsTitle}>
            {searchQuery
              ? `Результаты поиска (${items.length})`
              : `Всего в категории (${totalItems})`}
          </h2>

          {items.length === 0 ? (
            <div className={styles.emptyWrapper}>
              <EmptyState
                icon={MdCategory}
                title={searchQuery ? "Ничего не найдено" : "Нет товаров"}
                description={
                  searchQuery
                    ? "Попробуйте изменить поисковый запрос"
                    : "В этой категории пока нет товаров"
                }
              />
            </div>
          ) : (
            <ItemList
              items={items}
              getCategoryName={getCategoryName}
              isAuth={isAuth}
              usingStock={usingStock}
              onGroupPress={handleGroupPress}
              hasMore={hasMore}
              loadingMore={loadingMore}
              loadMore={loadMore}
            />
          )}
        </div>
      </div>
    </div>
  );
}
